import React from 'react';
import { BadgeIconPerfect } from './BadgeIcons';
import './BadgeIcons.css';

const LABEL = {
  podium: 'В яблочко',
  full: 'Полная обойма',
  fastest: 'Быстрее пули',
  regulars: 'В тир, как на работу'
};

const PLACE_METAL = {
  1: '#E6C35C',
  2: '#C0C0C0',
  3: '#C47A4A'
};

function placeMetal(place) {
  return PLACE_METAL[place] || PLACE_METAL[1];
}

export function dozenHallLabel(category) {
  return LABEL[category] || category;
}

/** Concentric target for the Скорострелы / В тир! hero. */
export function DozenTargetHero({ className = '' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="24" cy="24" r="22" fill="#F7F4EE" />
      <circle cx="24" cy="24" r="17" fill="#173662" />
      <circle cx="24" cy="24" r="12" fill="#F7F4EE" />
      <circle cx="24" cy="24" r="7.2" fill="#D7A35A" />
      <circle cx="24" cy="24" r="3.2" fill="#E6C35C" />
      <path
        d="M24 4v6M24 38v6M4 24h6M38 24h6"
        fill="none"
        stroke="#173662"
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.45"
      />
    </svg>
  );
}

/** Three standing shells with 1 / 2 / 3 — weekly score. */
export function DozenIconPodium({ className = '' }) {
  const shells = [
    { x: 14, y: 18, h: 28, fill: '#C0C0C0', n: '2' },
    { x: 32, y: 8, h: 38, fill: '#E6C35C', n: '1' },
    { x: 50, y: 20, h: 26, fill: '#C47A4A', n: '3' }
  ];
  return (
    <svg
      className={`badge-icon ${className}`.trim()}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      {shells.map((s) => (
        <g key={s.n}>
          <rect x={s.x - 6} y={s.y} width="12" height={s.h} rx="2.4" fill={s.fill} />
          <rect x={s.x - 7.4} y={s.y + s.h - 5} width="14.8" height="6" rx="1.6" fill={s.fill} />
          <rect x={s.x - 5} y={s.y + 2} width="10" height="5" rx="1.2" fill="#F7F4EE" opacity="0.55" />
          <circle cx={s.x} cy={s.y + 4.4} r="1.5" fill="#173662" opacity="0.35" />
          <text
            x={s.x}
            y={s.y + s.h - 10}
            textAnchor="middle"
            fill="#173662"
            fontSize="10"
            fontWeight="800"
            fontFamily="var(--font-display, Georgia, serif)"
          >
            {s.n}
          </text>
        </g>
      ))}
    </svg>
  );
}

/** Stopwatch with a streak — fastest full dozen. */
export function DozenIconFastest({ className = '', place = 1 }) {
  return (
    <svg
      className={`badge-icon ${className}`.trim()}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M8 22h12M6 30h12M9 38h10"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx="38" cy="36" r="18" fill={placeMetal(place)} stroke="#173662" strokeWidth="2" />
      <circle cx="38" cy="36" r="13" fill="#F7F4EE" />
      <circle cx="38" cy="36" r="4.2" fill={placeMetal(place)} />
      <circle cx="38" cy="36" r="1.6" fill="#173662" />
      <path d="M38 36 L38 26" stroke="#173662" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M38 36 L46 40" stroke="#C47A4A" strokeWidth="2" strokeLinecap="round" />
      <rect x="34" y="10" width="8" height="7" rx="1.6" fill="#F7F4EE" stroke="#173662" strokeWidth="1.4" />
      <path d="M30 14h-4M50 14h4" stroke="#173662" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/** Crate of twelve rounds — full dozens cleared. */
export function DozenIconFull({ className = '' }) {
  const rounds = [];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 4; col++) {
      rounds.push({ cx: 16 + col * 10.6, cy: 22 + row * 12 });
    }
  }
  return (
    <svg
      className={`badge-icon ${className}`.trim()}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="8" y="12" width="48" height="42" rx="6" fill="#8C5620" />
      <rect x="11" y="15" width="42" height="36" rx="4" fill="#B87428" />
      {rounds.map((r) => (
        <g key={`${r.cx}-${r.cy}`}>
          <circle cx={r.cx} cy={r.cy} r="4.2" fill="#E6C35C" />
          <circle cx={r.cx} cy={r.cy} r="1.5" fill="#F7F4EE" />
        </g>
      ))}
    </svg>
  );
}

/** Loaded clip. Metal is the place; the count stays in the row text. */
export function DozenFullBadge({ className = '', place = 1 }) {
  const metal = placeMetal(place);
  return (
    <svg
      className={`badge-icon ${className}`.trim()}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="12" y="26" width="40" height="26" rx="5" fill={metal} stroke="#173662" strokeWidth="2" />
      <rect x="16" y="32" width="32" height="8" rx="2" fill="#F7F4EE" opacity="0.35" />
      {[0, 1, 2, 3].map((i) => {
        const x = 16 + i * 9;
        return (
          <g key={i}>
            <rect x={x} y="10" width="7" height="20" rx="2" fill="#F7F4EE" stroke="#173662" strokeWidth="1.2" />
            <rect x={x} y="10" width="7" height="6" rx="1.4" fill="#173662" />
          </g>
        );
      })}
    </svg>
  );
}

/** Target disc with place number. Only the score podium uses this. */
export function DozenTargetMedal({ className = '', place = 1 }) {
  const fill = placeMetal(place);
  return (
    <svg
      className={`badge-icon ${className}`.trim()}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="32" cy="32" r="26" fill="#F7F4EE" />
      <circle cx="32" cy="32" r="20" fill="#173662" />
      <circle cx="32" cy="32" r="13" fill={fill} />
      <text
        x="32"
        y="38"
        textAnchor="middle"
        fill="#173662"
        fontSize="16"
        fontWeight="800"
        fontFamily="var(--font-display, Georgia, serif)"
      >
        {place}
      </text>
    </svg>
  );
}

export function DozenHallIcon({ category, className = '' }) {
  if (category === 'fastest') return <DozenIconFastest className={className} />;
  if (category === 'full') return <DozenIconFull className={className} />;
  if (category === 'regulars') return <BadgeIconPerfect className={className} />;
  if (category === 'podium') return <DozenIconPodium className={className} />;
  return null;
}
